import { formatDisplayDateTime } from '../../utils/format.js';
import { useUserOnboardingProgress } from '../../hooks/useOnboardingProgress.js';
import type { OnboardingStep } from '../../services/api/onboardingProgress.js';
import { StatusBadge } from './StatusBadge.js';
import { Spinner } from '../common/Spinner.js';

function fmt(value: string | null | undefined): string {
  const result = formatDisplayDateTime(value);
  return result === '-' ? '—' : result;
}

function StepGroup({ title, steps }: { title: string; steps?: Record<string, OnboardingStep> }) {
  const entries = steps ? Object.entries(steps) : [];
  if (entries.length === 0) return null;
  return (
    <div className="mt-4">
      <h4 className="text-sm font-medium text-gray-700 mb-2">{title}</h4>
      <div className="divide-y divide-gray-100">
        {entries.map(([key, step]) => (
          <div key={key} className="flex items-start gap-3 py-2">
            <span className="text-sm text-gray-700 min-w-[8rem] capitalize">
              {key.replace(/_/g, ' ')}
            </span>
            <StatusBadge state={step.status} size="sm" />
            <span className="text-xs text-gray-400 whitespace-nowrap">{fmt(step.updated_at)}</span>
            {step.notes ? <span className="text-xs text-gray-500">{step.notes}</span> : null}
          </div>
        ))}
      </div>
    </div>
  );
}

export function OnboardingProgressPanel({
  userId,
  embedded = false,
}: {
  userId: number;
  embedded?: boolean;
}) {
  const { data: progress, isLoading } = useUserOnboardingProgress(userId);

  const overall = progress?.completedAt ? 'completed' : progress ? 'in_progress' : null;
  const user = progress?.payload?.user;

  return (
    <div className={embedded ? '' : 'bg-white rounded-lg border border-gray-200 p-6'}>
      <div className={`flex items-center ${embedded ? 'justify-end' : 'justify-between'} mb-6`}>
        {!embedded && <h3 className="text-lg font-semibold text-gray-900">Onboarding Progress</h3>}
        {overall ? <StatusBadge state={overall} size="sm" /> : null}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : !progress ? (
        <div className="text-center py-8">
          <p className="text-sm text-gray-500">No onboarding progress recorded yet.</p>
          <p className="text-sm text-gray-400 mt-1">
            Progress appears here once the user runs the onboarding skill.
          </p>
        </div>
      ) : (
        <div>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
            <div>
              <dt className="text-gray-500">Type</dt>
              <dd className="text-gray-900 capitalize">{progress.userType ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Buddy</dt>
              <dd className="text-gray-900">{user?.buddy ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Values walkthrough</dt>
              <dd className="text-gray-900 capitalize">{progress.valuesWalkthrough ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Started</dt>
              <dd className="text-gray-900">{fmt(progress.startedAt)}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Last updated</dt>
              <dd className="text-gray-900">{fmt(progress.lastUpdatedAt)}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Completed</dt>
              <dd className="text-gray-900">{fmt(progress.completedAt)}</dd>
            </div>
          </dl>

          <StepGroup title="Steps" steps={progress.payload?.steps} />
          <StepGroup title="Admin — employee" steps={progress.payload?.admin_employee} />
          <StepGroup title="Admin — contractor" steps={progress.payload?.admin_contractor} />
        </div>
      )}
    </div>
  );
}
